import moment from 'moment';

/**
 * using a custom type in a Vue->props declaration produces
 * a console warning in dev envs (if the data is de-serialized
 * json) b/c Vue checks the type with instanceof. It's worth
 * getting the console warning to get auto-complete, etc.
 */

/**
 * iterates the keys of {from} and, for each key
 * that is unset/falsy on {to} adds the key/value to {to}.
 * if the value of any key is an Array or Object, the value
 * is copied by value.
 *
 * IDK why but I am having to call this in each constructor
 * or the app isn't working.
 *
 * @param to the object to add keys/values to
 * @param from the object to copy keys/values from
 */
function addUnsetProps(to, from) {
  if (typeof from !== 'object' || typeof to !== 'object') return;
  let keys = Object.keys(from);
  for (let i=0;i<keys.length;i++) {
    let key = keys[i];
    // don't set values that are already set
    if (to[key]) continue;
    // copy objects/arrays by value
    if (typeof from[key] === 'object' || Array.isArray(from[key])) {
      to[key] = makeCopy(from[key]);
    } else {
      to[key] = from[key];
    }
  }
}

/**
 * creates and returns a reference-free copy of {obj}
 * @param obj - the object/array to copy
 * @returns {Array|Object|boolean} returns a copy of {obj} if it exists
 */
function makeCopy(obj) {
  if (
    typeof obj !== 'undefined'
    && (typeof obj === 'object' || Array.isArray(obj))
  ) {
    return JSON.parse(JSON.stringify(obj));
  }
  return false;
}

/**
 * @param {object} src
 * @property { number } product_id
 * @property { number } company_id
 * @property { string } code
 * @property { number | null | undefined } gender
 * @property { number } active
 * @property { number } enrollment_kit
 * @property { number | null | undefined } min_age
 * @property { number | null | undefined } max_age
 * @property { boolean } animal_safe
 * @property { number | null | undefined } preg_or_nurse
 * @property { number } group_only_sale
 * @property { number } product_type_id
 * @property { string } internal_notes
 * @property { string | null | undefined } available_countries
 * @property { ProductType | Object } product_type
 * @property { Company | Object } company
 * @property { [Localization | Object] } localizations
 * @property { [ProductProperty | Object] } properties
 * @property { [CompanyGroup] } company_groups
 * @property { [Image] } images
 * @property { [CyclopediaProductProperty] } cyclopedia_product_properties
 * @property { [CyclopediaAnalysisCategory] } cyclopedia_analysis_categories
 * @property { Object } company_product_data
 * @constructor
 */
function Product(src = {}) {
  this.product_id = src.product_id || 0;
  this.company_id = src.company_id || 0;
  this.code = src.code || '';
  this.gender = src.gender || null;
  this.active = src.active || 0;
  this.enrollment_kit = src.enrollment_kit || 0;
  this.min_age = src.min_age || null;
  this.max_age = src.max_age || null;
  this.animal_safe = src.animal_safe || false;
  this.preg_or_nurse = src.preg_or_nurse || null;
  this.group_only_sale = src.group_only_sale || 0;
  this.product_type_id = src.product_type_id || 1;
  this.internal_notes = src.internal_notes || '';
  this.available_countries = src.available_countries || '';
  this.product_type = makeCopy(src.product_type) || {};
  this.company = makeCopy(src.company) || {};
  this.localizations = makeCopy(src.localizations) || [ ];
  this.properties = makeCopy(src.properties) || [ ];
  this.company_groups = makeCopy(src.company_groups) || [ ];
  this.images = makeCopy(src.images) || [ ];
  this.cyclopedia_product_properties = makeCopy(src.cyclopedia_product_properties) || [ ];
  this.cyclopedia_analysis_categories = makeCopy(src.cyclopedia_analysis_categories) || [ ];
  this.company_product_data = src.company_product_data || [];
  addUnsetProps(this, src);
}
function Language(src = {}) {
  this.language_id = src.id || 0;
  this.name = src.name || '';
  this.code = src.code || '';
  this.country = src.country || '';
  this.active = src.active || 0;
  addUnsetProps(this, src);
}
function Image(src = {}) {
  this.id = src.id || 0;
  this.name = src.name || '';
  this.path = src.path || '';
  this.image_size_id = src.image_size_id || 0;
  this.image_size = makeCopy(src.image_size) || {};
  this.image_type_id = src.image_type_id || 0;
  this.image_type = makeCopy(src.image_type) || {};
  addUnsetProps(this, src);
}
function Company(src = {}) {
  this.company_id = src.company_id || 0;
  this.name = src.name || '';
  this.tag = src.tag || '';
  this.active = src.active || 0;
  this.partner = src.partner || '';
  this.ranks = makeCopy(src.ranks) || [ ];
  this.groups = makeCopy(src.groups) || [ ];
  addUnsetProps(this, src);
}
function CompanyRank(src = {}) {
  this.id = src.id || 0;
  this.company_id = src.company_id || 0;
  this.company = makeCopy(src.company) || {};
  this.name = src.name || '';
  this.order = src.order || '';
  this.localizations = makeCopy(src.localizations) || [ ];
  addUnsetProps(this, src);
}
function CompanyRankLocalization(src = {}) {
  this.id = src.id || 0;
  this.company_rank_id = src.company_rank_id || 0;
  this.language_id = src.language_id || 0;
  this.language = makeCopy(src.language) || {};
  this.name = src.name || '';
  addUnsetProps(this, src);
}

/**
 * @param {object} src
 * @property { number } language_id
 * @property { number } product_id
 * @property { Language | Object } language
 * @property { string } product_name
 * @property { string } product_name_tm
 * @property { string } latin_name
 * @property { string } description
 * @constructor
 */
function Localization(src = {}) {
  this.language_id = src.language_id || 0;
  this.product_id = src.product_id || 0;
  this.language = makeCopy(src.language) || {};
  this.product_name = src.product_name || '';
  this.product_name_tm = src.product_name_tm || '';
  this.latin_name = src.latin_name || '';
  this.description = src.description || '';
  addUnsetProps(this, src);
}

/**
 * @param {object} src
 * @property { number } product_type_id
 * @property { string } name
 * @constructor
 */
function ProductType(src = {}) {
  this.product_type_id = src.product_type_id || 0;
  this.name = src.name || '';
  addUnsetProps(this, src);

}
/**
 * @param {object} src
 * @property { number } id
 * @property { number } product_id
 * @property { number } metric_id
 * @property { number } value_vlow
 * @property { number } value_low
 * @property { number } value_med
 * @property { number } value_vhi
 * @property { number } value_hi
 * @property { {id:number, name:string} | {} } metric
 * @constructor
 */
function FaceScanProductData(src = {}) {
  this.id = src.id || 0;
  this.product_id = src.product_id || 0;
  this.metric_id = src.metric_id || 0;
  this.value_vlow = src.value_vlow || 0;
  this.value_low = src.value_low || 0;
  this.value_med = src.value_med || 0;
  this.value_vhi = src.value_vhi || 0;
  this.value_hi = src.value_hi || 0;
  this.metric = makeCopy(src.metric) || { id: this.metric_id, name: '' };
  addUnsetProps(this, src);
}
function ProductProperty(src = {}) {
  this.id = src.id || 0;
  this.product_id = src.product_id || 0;
  this.language_id = src.language_id || 0;
  this.language = makeCopy(src.language) || {};
  this.title = src.title || '';
  this.body = src.body || '';
  addUnsetProps(this, src);
}
function Cyclopedia(src = {}) {
  this.id = src.id || 0;
  this.active = src.active || false;
  this.name = src.name || '';
  this.description = src.description || '';
  this.analyses = makeCopy(src.analyses) || [ ];
  this.companies = makeCopy(src.companies) || [ ];
  this.images = makeCopy(src.images) || [ ];
  this.dyk_products = makeCopy(src.dyk_products) || [];
  addUnsetProps(this, src);
}
function CyclopediaLocalization(src = {}) {
  this.id = src.id || 0;
  this.cyclopedia_id = src.cyclopedia_id || 0;
  this.language_id = src.language_id || 0;
  this.name = src.name || '';
  this.description = src.description || '';
  this.language = makeCopy(src.language) || {};
  addUnsetProps(this, src);
}
function CyclopediaAnalysis(src = {}) {
  this.id = src.id || 0;
  this.cyclopedia_id = src.cyclopedia_id || 0;
  this.cyclopedia = makeCopy(src.cyclopedia) || {};
  this.name = src.name || '';
  this.categories = makeCopy(src.categories) || [ ];
  addUnsetProps(this, src);
}
function CyclopediaAnalysisLocalization(src = {}) {
  this.id = src.id || 0;
  this.cyclopedia_analysis_id = src.cyclopedia_analysis_id || 0;
  this.analysis = makeCopy(src.analysis) || {};
  this.language_id = src.language_id || 0;
  this.language = makeCopy(src.language) || {};
  this.name = src.name || '';
  addUnsetProps(this, src);
}
function CyclopediaAnalysisCategory(src = {}) {
  this.id = src.id || 0;
  this.cyclopedia_analysis_id = src.cyclopedia_analysis_id || 0;
  this.analysis = makeCopy(src.analysis) || {};
  this.name = src.name || '';
  this.description = src.description || '';
  this.free_description = src.free_description || '';
  this.products = makeCopy(src.products) || [ ];
  addUnsetProps(this, src);
}
function CyclopediaAnalysisCategoryLocalization(src = {}) {
  this.id = src.id || 0;
  this.cyclopedia_analysis_category_id = src.cyclopedia_analysis_category_id || 0;
  this.category = makeCopy(src.category) || {};
  this.language_id = src.language_id || 0;
  this.language = makeCopy(src.language) || {};
  this.name = src.name || '';
  this.description = src.description || '';
  this.free_description = src.free_description || '';
  addUnsetProps(this, src);
}
function CyclopediaProductProperty(src = {}) {
  this.id = src.id || 0;
  this.language_id = src.language_id || 0;
  this.language = makeCopy(src.language) || {};
  this.cyclopedia_id = src.cyclopedia_id || 0;
  this.cyclopedia = makeCopy(src.cyclopedia) || {};
  this.title = src.title || '';
  this.body = src.body || '';
  addUnsetProps(this, src);
}
function CompanyGroup(src = {}) {
  this.id = src.id || 0;
  this.parent_company_id = src.parent_company_id || 0;
  this.company = makeCopy(src.company) || {};
  this.division_name = src.division_name || '';
  this.localizations = makeCopy(src.localizations) || [ ];
  this.products = makeCopy(src.products) || [ ];
  addUnsetProps(this, src);
}
function CompanyGroupLocalization(src = {}) {
  this.id = src.id || 0;
  this.language_id = src.language_id || 0;
  this.companydivisions_id = src.companydivisions_id || 0;
  this.division_name = src.division_name || '';
  this.language = makeCopy(src.language) || {};
  addUnsetProps(this, src);

}
function User(src = {}) {
  this.user_id = src.user_id || 0;
  this.invoices = makeCopy(src.invoices) || [{}];
  this.client = makeCopy(src.client) || {};
  this.clients = makeCopy(src.clients) || [{}];
  this.referrals = makeCopy(src.referrals) || [{}];
  addUnsetProps(this, src);
}
function Client(src = {}) {
  this.client_id = src.client_id || 0;
  this.user_id = src.user_id || 0;
  this.user = makeCopy(src.user) || {};
  addUnsetProps(this, src);
}
function ProductFrequency(src = {}) {
  this.id = src.id || 0;
  this.product_id = src.product_id || 0;
  this.product = makeCopy(src.product) || {};
  this.frequency = src.frequency || null;
  this.weight = src.weight || null;
  this.note = src.note || '';
  this.created_at = src.created_at || '';
  this.updated_at = src.updated_at || '';
  addUnsetProps(this, src);
}
function Invoice(src = {}) {
  this.invc_id = src.invc_id || 0;
  this.user_id = src.user_id || 0;
  this.creation_date = moment(src.creation_date) || null;
  this.creation_source = src.creation_source || "";
  this.subdata_json = src.subdata_json || {};
  this.coupon_id = src.coupon_id || 0;
  this.last_payt_id = src.last_payt_id;
  this.billing_first_name = src.billing_first_name || "";
  this.billing_last_name = src.billing_last_name || "";
  this.shipping_first_name = src.shipping_first_name || "";
  this.shipping_last_name = src.shipping_last_name || "";
  this.user = makeCopy(src.user) || {};
  this.transactions = makeCopy(src.transactions) || [];
  addUnsetProps(this, src);
}
function PaymentTransaction(src = {}) {
  this.payt_id = src.payt_id || 0;
  this.user_id = src.user_id || 0;
  this.invc_id = src.invc_id || 0;
  this.proc_id = src.proc_id || 0;
  this.proc_custprofile_id = src.proc_custprofile_id || null;
  this.proc_payprofile_id = src.proc_payprofile_id || null;
  this.proc_payprofile_data = src.proc_payprofile_data || "";
  this.invoice = makeCopy(src.invoice) || {};
  this.invoice.creation_date = moment(this.invoice.creation_date) || null;
  addUnsetProps(this, src);
}
function ProductDnaTraitScore(src = {}) {
  this.created_at = src.created_at || '';
  this.trait_id = src.trait_id || 0;
  this.product_id = src.product_id || 0;
  this.id = src.id || 0;
  this.score = src.score || '';
  this.dna_report = src.dna_report || false;
  this.itovi_report = src.itovi_report || false;
  this.updated_at = src.updated_at || '';
  addUnsetProps(this, src);
}
function ReferralCheck(src = {}) {
  this.id = src.id || 0;
  this.user_id = src.user_id || 0;
  this.status = src.status || "";
  this.amount = src.amount || 0;
  this.first_name = src.first_name || "";
  this.last_name = src.last_name || "";
  this.address = src.address || "";
  this.address_2 = src.address_2 || "";
  this.city = src.city || "";
  this.state = src.state || "";
  this.postal_code = src.postal_code || "";
  this.country = src.country || "";
  this.check_number = src.check_number || "";
  this.issue_date = src.issue_date || null;
  this.user = makeCopy(src.user) || {};
  this.referrals = makeCopy(src.referrals) || {};
  addUnsetProps(this, src);
}
function DnaTraitScoreDetail(src = {}) {
  this.traitId = src.traitId || 0;
  this.traitName = src.traitName || '';
  this.scoreName = src.scoreName || '';
  this.value = src.value || 0;
  addUnsetProps(this, src);
}
function ComposePushNotificationMessage(src = {}) {
  this.title = src.title || '';
  this.message = src.message || '';
  this.link = src.link || '';
  // this.fcm = src.fcm || '';
  addUnsetProps(this, src);
}

function SendPushesResponse(src = {}) {
  // this.queuedCount = src.queuedCount || 0;
  this.recipientCount = src.recipientCount || 0;
  this.successCount = src.successCount || 0;
  this.failureCount = src.failureCount || 0;
  this.invalidCount = src.invalidCount || 0;
  addUnsetProps(this, src);
}

/**
 * @param {object} src
 * @param { string } src.type
 * @param { Array<string> } src.fcm
 * @param { object } src.data
 * @param { string } src.message_title
 * @param { string } src.message_body
 * @param { number } src.badge
 *
 * @property { string } type
 * @property { Array<string> } fcm
 * @property { {} } data
 * @property { string } message_title
 * @property { string } message_body
 * @property { string } analytics_label
 * @property { number } badge
 * @constructor
 */
function PushNotification(src = {}) {
    this.type = src.type || 'notification';
    this.fcm = src.fcm || [];
    this.data = src.data || {};
    this.message_title = src.message_title || '';
    this.message_body = src.message_body || '';
    this.analytics_label = 'console';
    this.badge = Number.isFinite(src.badge) && src.badge >= 0 ? src.badge : 1;
}

/**
 * @param {object} src
 * @property { string } kitId
 * @property { string } status
 * @property { string } statusDate
 * @property { string } tracking
 * @property { number } userId
 * @property { { id: number, name: string } } heritage
 * @constructor
 */
function DnaKit(src = {}) {
  this.kitId = src.kitId || '';
  this.status = src.status || '';
  this.statusDate = src.statusDate || '';
  this.tracking = src.tracking || '';
  this.userId = src.userId || 0;
  this.heritage = src.heritage || {};
}

/**
 * @param {object} src
 * @property { string } kitId
 * @property { string } status
 * @property { string } statusDate
 * @property { [string] } purchasedReports
 * @property { number } userId
 * @property { number } traitCount
 * @property { number } reportCount
 * @constructor
 */
function FullDnaKit(src = {}) {
  this.kitId = src.kitId || '';
  this.status = src.status || '';
  this.statusDate = src.statusDate || '';
  this.userId = src.userId || 0;
  this.oldUserId = src.oldUserId || null;
  this.purchasedReports = src.purchasedReports || [];
  this.traitCount = src.traits ? src.traits.length : 0;
  this.reportCount = src.reports ? src.reports.length : 0;
}

/**
 * @param {object} src
 * @property { number } userId
 * @property { string } kitId
 * @property { string } status
 * @property { string } statusDate
 * @constructor
 */
function DnaOldKit(src = {}) {
  this.kitId = src.kitId || '';
  this.status = src.status || '';
  this.statusAt = src.statusAt || '';
}

/**
 * @param {object} src
 * @property { number } id
 * @property { string } status
 * @property { number } dnaKitId
 * @property { [] } genotypes
 * @property { [] } geneticReportDetails
 * @constructor
 */
function GeneticReport(src = {}) {
  this.id = src.id || 0;
  this.status = src.status || '';
  this.dnaKitId = src.dnaKitId || 0;
  this.genotypes = src.genotypes || [];
  this.geneticReportDetails = src.geneticReportDetails || [];
}

/**
 * @param {object} src
 * @property { string } kitId
 * @property { number } personId
 * @property { string } status
 * @property { string } statusAt
 * @property { number } dnaKitId
 * @property { [] } geneticReportIds
 * @property { [] } statusLogs
 * @constructor
 */
function GGKit(src = {}) {
  this.kitId = src.kitId || '';
  this.personId = src.personId || 0;
  this.status = src.status || '';
  this.statusAt = src.statusAt || '';
  this.geneticReportIds = src.geneticReportIds || [];
  this.statusLogs = src.statusLogs || [];
}

/**
 * @param {object} src
 * @property { Array<GGKit> } dnaKits
 * @property { number } id
 * @constructor
 */
function GdPerson(src = {}) {
  this.dnaKits = src.dnaKits || [];
  this.id = src.id || 0;

}

/**
 * @param {object} src
 * @property { Array<string> } dnaKits
 * @property { number } shipStationOrderId
 * @property { string } orderNumber
 * @property { { email:string } } shipTo
 * @constructor
 */
function PurchasedKits(src = {}) {
  this.dnaKits = src.dnaKits || [];
  this.shipStationOrderId = src.shipStationOrderId || 0;
  this.orderNumber = src.orderNumber || "";
  this.orderDate = src.orderDate || null;
  this.shipTo = src.shipTo || {};

}

/**
 *
 * @param src
 * @property { String } kitId
 * @property { String } first_name
 * @property { String } last_name
 * @property { String } email
 * @property { String } phone
 * @property { String } street1
 * @property { String } street2
 * @property { String } city
 * @property { String } country
 * @property { String } zip
 * @property { String } state_province
 * @constructor
 */
function DNAKitReplacementForm(src = {}) {
  this.kitId = src.kitId || "";
  this.first_name = src.first_name || "";
  this.last_name = src.last_name || "";
  this.email = src.email || "";
  this.phone = src.phone || "";
  this.address = {
    street1: src.street1 || "",
    street2: src.street2 || "",
    city: src.city || "",
    country_code: src.country_code || "",
    zip: src.zip || "",
    state_province: src.state_province || ""
  };
}

function DnaOrder(src = {}) {
  this.order_number = src.order_number || "";
  this.name = src.shipping_address.name || "";
  this.order_number = src.order_number || "";
  this.email = src.email || "";
  this.total_price = src.total_price || "";
  this.created_at = src.created_at || "";
  this.shipping_address = {
    address1: src.shipping_address.address1 || "",
    address2: src.shipping_address.address2 || "",
    city: src.shipping_address.city || "",
    state: src.shipping_address.state || "",
    province: src.shipping_address.province || "",
    zip: src.shipping_address.zip || "",
    country: src.shipping_address.country || "",
    country_code: src.shipping_address.country_code || "",
    province_code: src.shipping_address.province_code || "",
    phone: src.shipping_address.phone || ""
  };
}

export default {
  User: User,
  Product: Product,
  Language: Language,
  Image: Image,
  Company: Company,
  CompanyRank: CompanyRank,
  CompanyRankLocalization: CompanyRankLocalization,
  Localization: Localization,
  ProductType: ProductType,
  ProductFrequency: ProductFrequency,
  ProductProperty: ProductProperty,
  FaceScanProductData: FaceScanProductData,
  Cyclopedia: Cyclopedia,
  CyclopediaLocalization: CyclopediaLocalization,
  CyclopediaAnalysis: CyclopediaAnalysis,
  CyclopediaAnalysisCategory: CyclopediaAnalysisCategory,
  CyclopediaProductProperty: CyclopediaProductProperty,
  CompanyGroup: CompanyGroup,
  CompanyGroupLocalization: CompanyGroupLocalization,
  CyclopediaAnalysisLocalization: CyclopediaAnalysisLocalization,
  CyclopediaAnalysisCategoryLocalization: CyclopediaAnalysisCategoryLocalization,
  Client: Client,
  Invoice: Invoice,
  PaymentTransaction: PaymentTransaction,
  ProductDnaTraitScore: ProductDnaTraitScore,
  DnaTraitScoreDetail: DnaTraitScoreDetail,
  ComposePushNotificationMessage: ComposePushNotificationMessage,
  SendPushesResponse: SendPushesResponse,
  ReferralCheck: ReferralCheck,
  PushNotification: PushNotification,
  DnaKit: DnaKit,
  DnaOldKit: DnaOldKit,
  GeneticReport: GeneticReport,
  GGKit: GGKit,
  GdPerson: GdPerson,
  PurchasedKits: PurchasedKits,
  FullDnaKit: FullDnaKit,
  DNAKitReplacementForm: DNAKitReplacementForm,
  DnaOrder: DnaOrder
}
